import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import {
	applyGridPrefs,
	MIN_COLUMN_WIDTH,
} from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultCommoditiesGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";
import { mergeGridPrefs } from "~/shared/utils/merge-grid-prefs.ts";

const TRAILING_FIXED_FIELDS = ["isDefault"] as const;

type UseCommoditiesGridColumnsArgs = {
	/** Base Commodities columns from `INITIAL_COMMODITY_COLUMNS`. */
	baseColumns: BoardColumn[];
	isInternal: boolean;
	/** The user's saved Commodities column prefs (external users only). */
	columnPrefs?: GridPrefColumn[];
};

/**
 * Builds the Commodities grid columns by merging the code-defined defaults with
 * the user's persisted column preferences (visibility, order, width).
 * The trailing `isDefault` column is kept fixed ahead of the action column.
 */
export const useCommoditiesGridColumns = ({
	baseColumns,
	isInternal,
	columnPrefs,
}: UseCommoditiesGridColumnsArgs): BoardColumn[] => {
	return useMemo(() => {
		const trailingColumns = TRAILING_FIXED_FIELDS.flatMap((field) =>
			baseColumns.filter((col) => col.field === field),
		);
		const reorderableColumns = baseColumns.filter(
			(col) => !TRAILING_FIXED_FIELDS.includes(col.field as "isDefault"),
		);

		const defaults =
			getDefaultCommoditiesGridPrefs(isInternal)[GridIdentifiers.Commodities]
				?.columns ?? [];
		const mergedPrefs = mergeGridPrefs(defaults, columnPrefs ?? []);
		const mergedPrefsByField = new Map(
			mergedPrefs.map((column) => [column.columnId, column]),
		);

		const applied = applyGridPrefs(reorderableColumns, mergedPrefs, isInternal);
		const trailing = trailingColumns.map((col, index) => {
			const field = typeof col.field === "string" ? col.field : "";
			const pref = mergedPrefsByField.get(field);
			const rawWidth = Number(pref?.widthSize);
			const normalizedWidth = Number.isFinite(rawWidth)
				? Math.max(MIN_COLUMN_WIDTH, rawWidth)
				: col.width;

			return {
				...col,
				show: pref?.isVisible ?? col.show ?? true,
				width: normalizedWidth,
				reorderable: false,
				orderIndex: applied.length + index,
			};
		});

		return [...applied, ...trailing];
	}, [baseColumns, isInternal, columnPrefs]);
};
